import { createClient as createWSClient } from 'graphql-ws';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getUserAgent } from 'react-native-device-info';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  EnvironmentContext,
  EnvironmentContextProps,
  getWsUrl,
} from 'shared/providers/environment';
import { Environment } from 'shared/providers/environment.constants';

import {
  getSocketConnectionParams,
  socketImplementation,
} from './GqlWebSocket';

const useWsClient = () => {
  const { segmentClient } = useAnalyticsConfiguration();
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const { idToken, varoSessionId } = useContext<AuthContextProps>(AuthContext);
  const [userAgent, setUserAgent] = useState<string | undefined>(undefined);

  const retryCount = useRef(0);
  const activeSocket = useRef<WebSocket | undefined>(undefined);
  const timedOut = useRef<string | number | undefined>(undefined);
  const cancelBag = useRef<(() => void)[]>([]);

  useEffect(() => {
    const loadAgent = async () => {
      const agent = await getUserAgent();
      setUserAgent(agent);
    };
    if (currentEnv === Environment.PROD) {
      void loadAgent();
    } else {
      setUserAgent('varo-qe-auto');
    }
  }, [currentEnv]);

  const client = useMemo(
    () =>
      createWSClient({
        url: getWsUrl(currentEnv),
        connectionParams: () =>
          getSocketConnectionParams(userAgent, idToken, varoSessionId),
        lazy: true,
        keepAlive: 10000, // ping server every 10 seconds
        webSocketImpl: socketImplementation(userAgent, varoSessionId),
        connectionAckWaitTimeout: 50000,
        retryAttempts: 5,
        shouldRetry: () => true,
      }),
    [currentEnv, userAgent, idToken, varoSessionId],
  );

  useEffect(() => {
    const newCancelBag: (() => void)[] = [];

    newCancelBag.push(
      client.on('opened', (socket: WebSocket) => {
        activeSocket.current = socket;
      }),
    );

    newCancelBag.push(
      client.on('connected', (socket: WebSocket) => {
        activeSocket.current = socket;
        retryCount.current = 0;
      }),
    );

    newCancelBag.push(
      client.on('ping', (received) => {
        if (!received) {
          retryCount.current = retryCount.current + 1;
          // sent
          timedOut.current = setTimeout(() => {
            if (activeSocket.current?.readyState === WebSocket.OPEN) {
              activeSocket.current?.close(4408, 'Request Timeout');
              void segmentClient.logEvent('Error Encountered', {
                name: 'ws connection lost',
                retry_count: retryCount,
              });
            }
          }, 5000);
        } // wait 5 seconds for the pong and then close the connection
      }),
    );

    newCancelBag.push(
      client.on('pong', (received) => {
        if (received) {
          clearTimeout(timedOut.current);
          retryCount.current = 0;
        } // pong is received, clear connection close timeout
      }),
    );

    cancelBag.current = newCancelBag;

    return () => {
      cancelBag.current.forEach((cancel) => cancel());
      cancelBag.current = [];
    };
  }, [client, activeSocket, retryCount, timedOut, segmentClient, cancelBag]);

  return client;
};

export default useWsClient;
